import { useAutoneTranslation } from '@autone/translations';
import { createContext, type ReactNode, useContext } from 'react';

import { useAuthz } from '../hooks/useAuthz';

type AuthorizationContextValue = {
  hasReadOnlyAccess: boolean;
  hasAdminAccess: boolean;
  hasCurrentModuleAccess: boolean;
  disabledText: string;
  isAuthorized: (permissions: string[]) => boolean;
};

type Permissions = string[];

const AuthorizationContext = createContext<
  AuthorizationContextValue | undefined
>(undefined);

const useCanAccess = (
  editPermissions: Permissions,
  adminPermissions?: Permissions,
  currentModulePermissions?: Permissions,
): AuthorizationContextValue => {
  const { isAuthorized } = useAuthz();
  const { common } = useAutoneTranslation();

  const hasReadOnlyAccess = !isAuthorized(editPermissions);
  const hasAdminAccess = isAuthorized(adminPermissions || []);
  const hasCurrentModuleAccess = isAuthorized(currentModulePermissions || []);

  return {
    hasReadOnlyAccess,
    hasAdminAccess,
    hasCurrentModuleAccess: hasCurrentModuleAccess,
    disabledText: common('tooltips.no-access-text'),
    isAuthorized,
  };
};

export const AuthorizationProvider = ({
  children,
  editPermissions,
  adminPermissions,
  currentModulePermissions,
}: {
  children: ReactNode;
  editPermissions: string[];
  adminPermissions?: string[];
  currentModulePermissions?: string[];
}) => {
  const access = useCanAccess(
    editPermissions,
    adminPermissions,
    currentModulePermissions,
  );

  return (
    <AuthorizationContext.Provider value={access}>
      {children}
    </AuthorizationContext.Provider>
  );
};

export const useAuthorization = () => {
  const context = useContext(AuthorizationContext);
  if (context === undefined) {
    throw new Error(
      'useAuthorization must be used within an AuthorizationProvider',
    );
  }
  return context;
};
